import axios from '../utils/setupAxiosAuth';
import API_ROUTES from '../routes/routes';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import {
  AssistantTurnResponse,
  MessageInboxConversation,
  MessageThread,
  TwilioMessage,
} from '../types/messageInbox';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('user_authToken')}`,
});

export const fetchConversations = async (params: Record<string, string | number>) => {
  const response = await axios.get(`${getApiBaseUrl()}${API_ROUTES.messageInbox}`, {
    headers: authHeaders(),
    params,
  });
  return {
    data: response.data as MessageInboxConversation[],
    total: parseInt(response.headers['x-total-count'] || '0', 10),
  };
};

export const fetchConversationThread = async (conversationKey: string) => {
  const response = await axios.get(
    `${getApiBaseUrl()}${API_ROUTES.messageInbox}/${conversationKey}`,
    { headers: authHeaders() }
  );
  return response.data as MessageThread;
};

export const sendManualReply = async (conversationKey: string, formData: FormData) => {
  const response = await axios.post(
    `${getApiBaseUrl()}${API_ROUTES.messageInbox}/${conversationKey}/reply`,
    formData,
    {
      headers: {
        ...authHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data as TwilioMessage;
};

export const syncFromTwilio = async (days?: number) => {
  const response = await axios.post(
    `${getApiBaseUrl()}${API_ROUTES.messageInbox}/sync`,
    days ? { days } : {},
    { headers: authHeaders() }
  );
  return response.data as {
    fetched: number;
    inboxUpserted: number;
    skipped: number;
    days: number;
    limit: number;
  };
};

export const generateAssistantReply = async (thread: MessageThread) => {
  const latestInbound = [...thread.messages]
    .reverse()
    .find((message) => message.direction === 'inbound' && message.body);
  const latestMessage = latestInbound || [...thread.messages].reverse().find((message) => message.body);

  const response = await axios.post(
    `${getApiBaseUrl()}${API_ROUTES.assistant}/turn`,
    {
      channel: 'whatsapp',
      conversation_id: thread.conversation_key,
      message: latestMessage?.body || '',
      contact_phone: thread.contact_phone,
      customer_id: thread.customer_id || null,
      customer_name: thread.customer_name || thread.contact_name || null,
      product_context: thread.product_context || [],
      last_messages: thread.messages.slice(-8).map((message) => ({
        direction: message.direction,
        body: message.body || '',
        dt_created: message.dt_created,
      })),
      context: {
        watchlist: thread.watchlist || [],
      },
      execute_actions: false,
    },
    { headers: authHeaders() }
  );

  return response.data as AssistantTurnResponse;
};

export interface WhatsAppDashboardStats {
  summary: {
    total_inbound: number;
    total_outbound: number;
    total_sent: number;
    total_delivered: number;
    total_read: number;
    total_failed: number;
    total_undelivered: number;
    total_contacts: number;
    total_conversations: number;
    total_customer_linked: number;
    total_manual_replies: number;
    total_ai_replies: number;
  };
  daily: Array<{
    date: string;
    inbound: number;
    outbound: number;
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    undelivered: number;
    contacts: number;
    conversations: number;
    manual_replies: number;
    ai_replies: number;
  }>;
  templates: Array<{
    template_id: string;
    sent_count: number;
    delivered_count: number;
    read_count: number;
    failed_count: number;
  }>;
}

export const fetchWhatsAppDashboardStats = async (startDate?: string, endDate?: string) => {
  const response = await axios.get(`${getApiBaseUrl()}${API_ROUTES.messageInbox}/dashboard/stats`, {
    headers: authHeaders(),
    params: { startDate, endDate },
  });
  return response.data as WhatsAppDashboardStats;
};
